import React, { useMemo } from 'react';
import { Text } from 'rsuite';
import {
  PresentationCol,
  PresentationGrid,
  PresentationRow,
} from '../../../layout/PresentationLayout';
import { PAYMENT_METHODS } from '../../../../common/enums';
import { PaymentMethod } from 'src/types/payment';
import { PaymentMethodButton } from './PaymentMethodButton';

interface ControlledPaymentSelectorProps {
  achEnabled?: boolean;
  ccVerbiage?: string;
  ccEnabled?: boolean;
  checkEnabled?: boolean;
  onSelectPaymentMethod: (method: PaymentMethod) => void;
  paymentMethod?: PaymentMethod;
  singlePaymentButton?: boolean;
}

export const ControlledPaymentMethodSelector = ({
  achEnabled,
  ccEnabled,
  ccVerbiage,
  checkEnabled,
  onSelectPaymentMethod,
  paymentMethod,
  singlePaymentButton,
}: ControlledPaymentSelectorProps) => {
  const multipleEnabled = useMemo(() => {
    // Special case: when singlePaymentButton is true and only CC+ACH are enabled
    if (singlePaymentButton && ccEnabled && achEnabled && !checkEnabled) {
      return false;
    }

    // Normal case: count enabled payment methods
    const enabled = [ccEnabled, achEnabled, checkEnabled].filter(Boolean);

    return enabled.length > 1;
  }, [ccEnabled, achEnabled, checkEnabled, singlePaymentButton]);

  const ccButtonIcon = useMemo(() => {
    const icons = ['/assets/credit-card-icon.svg'];
    if (singlePaymentButton && achEnabled) {
      icons.push('/assets/bank-building-ACH-direct-debit.svg');
    }
    return icons;
  }, [singlePaymentButton, achEnabled]);

  const ccLabel = useMemo(() => {
    if (singlePaymentButton && achEnabled) {
      return 'Credit Card / ACH';
    }
    return ccVerbiage || 'Credit or Debit Card';
  }, [ccVerbiage, singlePaymentButton, achEnabled]);

  return (
    <PresentationGrid fluid>
      <PresentationRow className="mb-8">
        <PresentationCol xs={24}>
          <Text
            as="h6"
            className="text-small text-bold"
          >
            Payment Method
          </Text>
        </PresentationCol>
      </PresentationRow>

      {multipleEnabled && (
        <PresentationRow>
          {achEnabled && !singlePaymentButton && (
            <PaymentMethodButton
              icon="/assets/bank-building-ACH-direct-debit.svg"
              name="ACH Direct Debit"
              isActive={paymentMethod === PAYMENT_METHODS.ACH_DIRECT_DEBIT}
              onClick={() =>
                onSelectPaymentMethod(PAYMENT_METHODS.ACH_DIRECT_DEBIT)
              }
            />
          )}

          {ccEnabled && (
            <PaymentMethodButton
              icon={ccButtonIcon}
              name={ccLabel}
              isActive={paymentMethod === PAYMENT_METHODS.CREDIT_OR_DEBIT__CARD}
              onClick={() =>
                onSelectPaymentMethod(PAYMENT_METHODS.CREDIT_OR_DEBIT__CARD)
              }
            />
          )}
          {checkEnabled && (
            <PaymentMethodButton
              icon="/assets/cheque-sign-icon.svg"
              name="Paper Check"
              isActive={paymentMethod === PAYMENT_METHODS.CHECK}
              onClick={() => onSelectPaymentMethod(PAYMENT_METHODS.CHECK)}
            />
          )}
        </PresentationRow>
      )}
    </PresentationGrid>
  );
};
